import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { userService } from "../services/userService";
import {
  THEME_STORAGE_KEY as STORAGE_KEY,
  THEME_DARK as DARK,
  THEME_LIGHT as LIGHT,
  normalizeTheme,
  readStoredTheme,
  applyThemeToDocument,
  themeFromAuthResponse,
} from "../utils/themePreference";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // Se já há sessão de UI, alinha tema com o backend (cookie).
  useEffect(() => {
    let cancelled = false;
    let hasSession = false;
    try {
      hasSession = Boolean(
        sessionStorage.getItem("userData") ||
          sessionStorage.getItem("usuario_id")
      );
    } catch {
      hasSession = false;
    }
    if (!hasSession) return undefined;

    userService
      .getTheme()
      .then((data) => {
        if (cancelled) return;
        const next = normalizeTheme(data?.theme);
        setThemeState(next);
      })
      .catch(() => {
        /* offline / 401 — mantém local */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const setTheme = useCallback((nextTheme) => {
    setThemeState(normalizeTheme(nextTheme));
  }, []);

  const applyThemeFromAuth = useCallback((authData) => {
    const next = themeFromAuthResponse(authData);
    setThemeState(next);
    return next;
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === DARK ? LIGHT : DARK));
  }, []);

  /**
   * Atualiza UI na hora e persiste no backend.
   * Em erro de rede, reverte o tema local e rejeita a promise.
   */
  const persistTheme = useCallback(async (nextTheme) => {
    const previous = themeRef.current;
    const next = normalizeTheme(nextTheme);
    setThemeState(next);

    try {
      const data = await userService.updateTheme(next);
      const saved = normalizeTheme(data?.theme ?? next);
      setThemeState(saved);
      return { theme: saved };
    } catch (error) {
      // Sem sessão (401) ou rede: mantém tema local aplicado; só reverte se for erro de validação.
      if (error?.response?.status && error.response.status !== 401) {
        setThemeState(previous);
      }
      throw error;
    }
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === DARK,
      isLight: theme === LIGHT,
      setTheme,
      toggleTheme,
      persistTheme,
      applyThemeFromAuth,
    }),
    [theme, setTheme, toggleTheme, persistTheme, applyThemeFromAuth]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode deve ser usado dentro de ThemeProvider");
  }
  return context;
}

export { DARK, LIGHT, STORAGE_KEY, normalizeTheme, applyThemeToDocument };
